import { Button, CustomFilter, EllipsisSpan, Form, Input, Modal, Paging, Table, usePaging } from '@maxtropy/components';
import { Space } from 'antd';
import React, { Key, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

export interface AssociatedDevice {
  id?: number;
  deviceId?: number;
  name?: string;
  deviceName?: string;
  code?: string;
  deviceCode?: string;
  sn?: string;
  serialNumber?: string;
  typeName?: string;
  customerName?: string;
  deviceTags?: string[];
  tags?: string[];
  deviceTag?: string;
  tag?: string;
}

export interface AssociatedDevicePageRequestParams {
  timestamp?: number;
  codeOrName?: string;
  nameOrCode?: string;
  typeIds?: number[];
  deviceType?: number[];
  page?: number;
  size?: number;
  pageSize?: number;
}

export type AssociatedDevicePageRequest = (
  requestData: AssociatedDevicePageRequestParams
) => Promise<{ total?: number; list?: AssociatedDevice[] }>;

interface AssociatedDeviceModalProps {
  title?: string;
  typeIds: number[];
  selectionType?: 'checkbox' | 'radio';
  selectedDevices?: AssociatedDevice[];
  requestDevicePage: AssociatedDevicePageRequest;
  onOk: (devices: AssociatedDevice[]) => void;
  onCancel: () => void;
}

interface FilterProps {
  codeOrName?: string;
}

const formatDeviceTags = (record: AssociatedDevice) => {
  const tags = record.deviceTags ?? record.tags;
  if (tags?.length) {
    return tags.join('、');
  }

  return record.deviceTag ?? record.tag ?? '--';
};

const getDeviceId = (record: AssociatedDevice) => record.id ?? record.deviceId;

const getDeviceCode = (record: AssociatedDevice) => record.code ?? record.deviceCode;

const getDeviceName = (record: AssociatedDevice) => record.name ?? record.deviceName;

const getDeviceRowKey = (record: AssociatedDevice) => String(getDeviceId(record) ?? getDeviceCode(record) ?? '');

const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    width: 180,
    render: (v: string, record: AssociatedDevice) => <EllipsisSpan value={v ?? getDeviceCode(record) ?? '--'} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    width: 180,
    render: (v: string, record: AssociatedDevice) => <EllipsisSpan value={v ?? getDeviceName(record) ?? '--'} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    width: 120,
    render: (v: string, record: AssociatedDevice) => <EllipsisSpan value={v ?? record.serialNumber ?? '--'} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'deviceTags',
    width: 180,
    render: (_: string[] | undefined, record: AssociatedDevice) => <EllipsisSpan value={formatDeviceTags(record)} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const AssociatedDeviceModal: React.FC<AssociatedDeviceModalProps> = ({
  title = '关联设备',
  typeIds,
  selectionType = 'checkbox',
  selectedDevices,
  requestDevicePage,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<FilterProps>();
  const [selectedDeviceMap, setSelectedDeviceMap] = useState<Record<string, AssociatedDevice>>({});

  useEffect(() => {
    const nextMap: Record<string, AssociatedDevice> = {};
    selectedDevices?.slice(0, selectionType === 'radio' ? 1 : undefined).forEach(item => {
      const deviceKey = getDeviceRowKey(item);

      if (deviceKey) {
        nextMap[deviceKey] = item;
      }
    });
    setSelectedDeviceMap(nextMap);
  }, [selectedDevices, selectionType]);

  const { data, isLoading } = useQuery(
    ['associatedDevicePage', pageOffset, pageSize, searchParams, typeIds],
    async () => {
      if (!typeIds.length) {
        setTotalCount(0);
        return [];
      }
      const res = await requestDevicePage({
        ...searchParams,
        timestamp: Date.now(),
        nameOrCode: searchParams?.codeOrName,
        typeIds,
        deviceType: typeIds,
        page: pageOffset,
        size: pageSize,
        pageSize,
      });
      setTotalCount(res.total ?? 0);
      return (res.list ?? []).filter(item => getDeviceRowKey(item));
    }
  );

  const selectedRowKeys = useMemo(() => Object.keys(selectedDeviceMap), [selectedDeviceMap]);
  const selectedList = useMemo(() => Object.values(selectedDeviceMap), [selectedDeviceMap]);

  const onFinish = (v: FilterProps) => {
    setSearchParams(v);
    setPageOffset(1);
  };

  const onReset = () => {
    form.resetFields();
    setSearchParams(undefined);
    setPageOffset(1);
  };

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (keys: Key[]) => {
      setSelectedDeviceMap(map => {
        const currentPageMap =
          data?.reduce<Record<string, AssociatedDevice>>((nextMap, item) => {
            nextMap[getDeviceRowKey(item)] = item;
            return nextMap;
          }, {}) ?? {};
        const nextMap: Record<string, AssociatedDevice> = {};

        const nextKeys = selectionType === 'radio' ? keys.slice(-1) : keys;

        nextKeys.forEach(key => {
          const deviceKey = String(key);
          const currentRow = currentPageMap[deviceKey];
          const cachedRow = map[deviceKey];

          if (currentRow || cachedRow) {
            nextMap[deviceKey] = currentRow ?? cachedRow;
          }
        });

        return nextMap;
      });
    },
    onSelect: (record: AssociatedDevice, selected: boolean) => {
      setSelectedDeviceMap(map => {
        const deviceKey = getDeviceRowKey(record);
        const nextMap = selectionType === 'radio' ? {} : { ...map };

        if (selected) {
          nextMap[deviceKey] = record;
        } else {
          delete nextMap[deviceKey];
        }

        return nextMap;
      });
    },
    onSelectAll: (selected: boolean, _selectedRows: AssociatedDevice[], changeRows: AssociatedDevice[]) => {
      if (selectionType === 'radio') {
        return;
      }

      setSelectedDeviceMap(map => {
        const nextMap = { ...map };

        changeRows.forEach(record => {
          const deviceKey = getDeviceRowKey(record);

          if (selected) {
            nextMap[deviceKey] = record;
          } else {
            delete nextMap[deviceKey];
          }
        });

        return nextMap;
      });
    },
  };

  return (
    <Modal
      title={title}
      open
      size="big"
      maskClosable={false}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" onClick={() => onOk(selectedList)}>
            确定
          </Button>
        </Space>
      }
    >
      <CustomFilter colSpan={8} onFinish={v => onFinish(v as FilterProps)} form={form} onReset={onReset}>
        <Form.Item name="codeOrName" label="编号/名称">
          <Input placeholder="请输入编号或名称查询" />
        </Form.Item>
      </CustomFilter>
      <Table
        loading={isLoading}
        sticky
        rowSelection={{
          type: selectionType,
          ...rowSelection,
        }}
        selectedCount={selectedList.length}
        showSelectedCount
        dataSource={data}
        rowKey={getDeviceRowKey}
        scroll={{ y: 300 }}
        columns={columns}
      />
      <Paging pagingInfo={pagingInfo} />
    </Modal>
  );
};

export default AssociatedDeviceModal;
